import React, { useContext, useEffect, useRef, useState } from 'react';
import { DrawingProvider, VideoPlayer } from '@producer/ui-kit';
import { observer } from 'mobx-react-lite';

import { LoaderContainer } from '../step-container';
import { PageLoader } from '../../../../components/page-loader/PageLoader';
import { VersionSelector } from '../../../../features/assets/components/version-selector';
import { timeDifference } from '../../../../lib/utils/TimeDifference';
import { Comments } from '../../../../components/comment/Comments';
import { CompareContext } from './compare.context';
import { secondsToHms } from '../../../../lib/utils/Convertor';
import { CommentProvider } from '../../../../core/contexts/comment-context/comment.provider';
import { Asset } from '../../../../app/entities/asset';

import styles from './video-to-compare.module.scss';
type VideoCompareProps = {
  id: 'v1' | 'v2';
  selectedAssetId: string;
  onSelectVersion: (id: 'v1' | 'v2', versionId: string) => void;
  assetChildren: Asset[];
};

export const VideoToCompare = observer(
  React.forwardRef<HTMLVideoElement, VideoCompareProps>(
    ({ id, selectedAssetId, onSelectVersion, assetChildren }, ref) => {
      const playerRef = ref as React.RefObject<HTMLVideoElement>;
      const timeTagRef = useRef<HTMLDivElement | null>(null);

      const selectedAsset = Asset.getOne(selectedAssetId);
      const { togglePlay, toggleMute, handleSeekTime, handleRateChange, handleLoadStart } =
        useContext(CompareContext);

      const [videoLink, setVideoLink] = useState<null | string>(null);
      const [isLoading, setIsLoading] = useState(true);

      useEffect(() => {
        if (!selectedAsset) {
          return;
        }

        selectedAsset.fetchComments();

        selectedAsset
          .getPlaybackLink()
          .then((url) => {
            setVideoLink(url);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }, [selectedAsset]);

      if (isLoading || !selectedAsset) {
        return (
          <LoaderContainer>
            <PageLoader />
          </LoaderContainer>
        );
      }

      return (
        <DrawingProvider>
          <CommentProvider comments={selectedAsset.comments}>
            <div className={styles['container']}>
              <div className={styles['video-player-wrapper']}>
                {videoLink && (
                  <VideoPlayer
                    key={`video-player-${id}-${selectedAsset._id}`}
                    ref={playerRef}
                    url={videoLink}
                    onLoadStart={() => handleLoadStart(playerRef)}
                    onRateChange={handleRateChange}
                    onSetCurrentTime={handleSeekTime}
                    onMuteChange={(isMute) => toggleMute(playerRef, isMute)}
                    onPlayChange={(isPlaying) => togglePlay(playerRef, isPlaying)}
                    onTimeUpdate={(currentTime) => {
                      if (!timeTagRef?.current) {
                        return;
                      }

                      timeTagRef.current.innerHTML = secondsToHms(currentTime);
                    }}
                  />
                )}

                <div className={styles['video-info']}>
                  <div className={styles['asset-name']}>{selectedAsset.name}</div>

                  {assetChildren?.length > 0 && (
                    <VersionSelector
                      key={`video-asset-selector-${id}-${selectedAsset._id}`}
                      assetChildren={assetChildren}
                      version={selectedAsset.version!}
                      selectedAssetId={selectedAsset._id}
                      onSelect={(asset) => onSelectVersion(id, asset._id)}
                    />
                  )}

                  <div className={styles['relative-time']}>
                    {timeDifference(new Date(), selectedAsset.createdAt)}
                  </div>
                </div>
              </div>

              <div className={styles['comments-wrapper']}>
                <Comments
                  asset={selectedAsset}
                  showHeader={false}
                  timeTagRef={timeTagRef}
                  defaultShowCompleted
                />
              </div>
            </div>
          </CommentProvider>
        </DrawingProvider>
      );
    },
  ),
);
